import { Question } from './../../models/campaign/question';
import { CampaignInfo } from './../../models/campaign/campaign-info';
import { CampaignSectionFilter } from './../../../pages/landing/campaign-section/campaign-section-filter';
import { environment } from './../../../../environments/environment';
import { PagedResult } from './../../../shared/models/paged-result/paged-result';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { StorageService } from '../storage.service';
import { BusinessInfo } from '../../models/campaign/business-info';
import moment from 'moment';
import { CampaignNewsModel } from '../../models/campaign/campaign-news.model';
import { CampaignNews } from '../../models/campaign/campaign-news';
import { CampaignEventModel } from '../../models/campaign/campaign-event.model';
import { CampaignEvent } from '../../models/campaign/campaign-event';
import { InvestmentModel } from '../../models/investment/investment.model';
import { AccountService } from './account.service';
import { Payment, WalletPayment } from '../../models/payment/payment';
import { PaymentModel, SimplePaymentModel } from '../../models/payment/payment.model';
import { Campaign } from '../../models/campaign/campaign';
import { MyInvestment } from '../../models/investment/my-investment';
import { Edd } from '../../models/investment/edd';
import { FinancialOverview } from '../../models/campaign/financial-overview';
import { StatisticCount } from '../../models/campaign/statistic-count';
import { Role } from '../../enum/role.enum';
import { CampaignWhatsNew } from '../../models/campaign/campaign-whats-new';
import { InvestmentFilterType } from '../../enum/investment-filter-type.enum';
import { ShareType } from '../../enum/share-type.enum';
import { SortDirection } from '../../enum/sort-direction.enum';
import { CampaignSummary } from '../../models/campaign/campaign-summary';

@Injectable()
export class CampaignService {
  private route = environment.apiServer + 'Api/v1/ECF/{role}/campaigns';
  statisticCount: ReplaySubject<StatisticCount> = new ReplaySubject<StatisticCount>(1);

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private accountService: AccountService,
  ) {}

  getCampaigns(filter: CampaignSectionFilter): Observable<PagedResult<Campaign>> {
    const params: any = Object.assign({}, filter);

    if (params.orderBy && params.orderBy != 0) {
      params.orderBy = InvestmentFilterType[params.orderBy];
      params.IsOrderByDesc = true;
    } else delete params.orderBy;

    if (!params.filterString) delete params.filterString;

    // Split the string into array before pass to BE
    if (params.partnerIds) {
      params.partnerIds = params.partnerIds.split(',');
    }

    if (params.partnerSlugs) {
      params.partnerSlugs = params.partnerSlugs.split(',');
    }

    // If `selectedSectors` is empty, don't override the `sectorIds`
    // as it might be from the url
    if (params.selectedSectors && params.selectedSectors.length > 0) {
      params.sectorIds = params.selectedSectors.map((sector) => sector.id || sector.key);

      delete params.selectedSectors;
    }

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route, { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data ? data.result.data : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  getCampaign(slug: string): Observable<Campaign> {
    const params =
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
        ? { 'noToken': 'noToken' }
        : null;

    return this.http.get(this.route + '/' + slug, { params: params }).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }
  getCampaignSummary(campaignId: number): Observable<CampaignSummary> {
    const params =
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
        ? { 'noToken': 'noToken' }
        : null;

    return this.http
      .get(this.route + '/' + campaignId + '/campaignSummary', { params: params })
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  finalizeCampaign(campaignId: number): Observable<{ campaignId: number; isFinalized: boolean }> {
    return this.http.put(this.route + '/' + campaignId + '/finalize', null).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getMyInvestment(slug: string): Observable<MyInvestment> {
    return this.http.get(this.route + '/' + slug + '/myinvestments').pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getStatisticCount(campaignId: number): Observable<StatisticCount> {
    const params = {
      noToken: 'noToken',
    };

    return this.http
      .get(this.route + '/' + campaignId + '/statisticcount', { params: params })
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  getCampaignWhatsNew(campaignId: number): Observable<CampaignWhatsNew> {
    const params = {
      noToken: 'noToken',
    };

    return this.http
      .get(environment.apiServer + 'Api/v1/ECF/public/campaigns/' + campaignId + '/whatsNew', {
        params: params,
      })
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  getBusinessInfo(campaignId: number): Observable<BusinessInfo> {
    const params =
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
        ? { 'noToken': 'noToken' }
        : null;

    return this.http.get(this.route + '/' + campaignId + '/businessInfo', { params: params }).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getCampaignInfo(campaignId: number): Observable<CampaignInfo> {
    const params =
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
        ? { 'noToken': 'noToken' }
        : null;

    // use `switchMap` to handle both async calls
    // use `catchError` to handle incase one endpoint fails, the code will still returning the result
    return this.http.get(this.route + '/' + campaignId + '/info', { params: params }).pipe(
      catchError(() => of(null)),
      switchMap((data: any) => {
        return this.getCampaignWhatsNew(campaignId).pipe(
          catchError(() => of(null)),
          map((data2: any) => {
            data.result.campaignWhatsNew = data2;
            return data.result;
          }),
        );
      }),
    );
  }

  getCampaignNews(
    campaignId: number,
    currentPage: number,
    take: number,
    isPublished: boolean,
  ): Observable<PagedResult<CampaignNews>> {
    let params = {
      currentPage: currentPage.toString(),
      take: take.toString(),
      isPublished: isPublished.toString(),
    };

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route + '/' + campaignId + '/news', { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data ? data.result.data : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  getCampaignNewsUnreadCount(campaignId: number): Observable<number> {
    let params = {
      lastViewDateTime: this.storageService.lastViewDateTime || moment().toISOString(),
    };

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    this.storageService.lastViewDateTime = moment().toISOString();

    return this.http
      .get(this.route + '/' + campaignId + '/news/unreadCount', { params: params })
      .pipe(
        map((data: any) => {
          return data.result ? data.result.unviewedCount : 0;
        }),
      );
  }

  getQuestions(
    campaignId: number,
    currentPage: number,
    take: number,
    questionId?: number,
    parentQuestionId?: number,
  ): Observable<PagedResult<Question>> {
    let params: any = {
      currentPage: currentPage.toString(),
      take: take.toString(),
    };

    if (questionId) params.QuestionId = questionId;

    if (parentQuestionId) params.parentQuestionId = parentQuestionId;

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route + '/' + campaignId + '/questions', { params: params }).pipe(
      map((data: any) => {
        let result = data.result.data ? data.result.data : [];

        if (result.length > 0) {
          result.forEach((question) => {
            if (question.answers && question.answers.data && question.answers.data.length > 0) {
              question.answers = {
                data:
                  question.answers && question.answers.data.length > 0 ? question.answers.data : [],
                total: question.answers.totalCount,
              };
            }

            let subresult = question.answers && question.answers.data ? question.answers.data : [];

            if (subresult.length > 0) {
              subresult.forEach((answer) => {
                if (answer.answers && answer.answers.data && answer.answers.data.length > 0) {
                  answer.answers = {
                    data:
                      answer.answers && answer.answers.data.length > 0 ? answer.answers.data : [],
                    total: answer.answers.totalCount,
                  };
                }
              });
            }
          });
        }

        return {
          data: result,
          total: data.result.totalCount,
        };
      }),
    );
  }

  getAnswers(
    campaignId: number,
    questionId: number,
    skip: number,
    take: number,
  ): Observable<PagedResult<Question>> {
    const params = {
      skip: skip.toString(),
      take: take.toString(),
    };

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http
      .get(this.route + '/' + campaignId + '/questions/' + questionId + '/answers', {
        params: params,
      })
      .pipe(
        map((data: any) => {
          let result = data.result.data ? data.result.data : [];

          if (result.length > 0) {
            result.forEach((subanswer) => {
              if (
                subanswer.answers &&
                subanswer.answers.data &&
                subanswer.answers.data.length > 0
              ) {
                subanswer.answers = {
                  data:
                    subanswer.answers && subanswer.answers.data.length > 0
                      ? subanswer.answers.data
                      : [],
                  total: subanswer.answers.totalCount,
                };
              }
            });
          }

          return {
            data: result,
            total: data.result.totalCount,
          };
        }),
      );
  }

  addQuestion(campaignId: number, text: string): Observable<any> {
    const body = {
      text: text,
    };

    return this.http.post(this.route + '/' + campaignId + '/questions', body);
  }

  addAnswer(campaignId: number, questionId: number, text: string): Observable<any> {
    const body = {
      text: text,
    };

    return this.http.post(
      this.route + '/' + campaignId + '/questions/' + questionId + '/answers',
      body,
    );
  }

  deleteQuestion(questionId: number, campaignId: number): Observable<any> {
    return this.http.delete(this.route + '/' + campaignId + '/questions/' + questionId);
  }

  deleteAnswer(answerId: number, questionId: number, campaignId: number): Observable<any> {
    return this.http.delete(
      this.route + '/' + campaignId + '/questions/' + questionId + '/answers/' + answerId,
    );
  }

  createCampaignNews(campaignId: number, news: CampaignNewsModel): Observable<any> {
    const body = news;

    return this.http.post(this.route + '/' + campaignId + '/news', body);
  }

  updateCampaignNews(
    campaignId: number,
    campaignNewsId: number,
    news: CampaignNewsModel,
  ): Observable<any> {
    const body = news;

    return this.http.put(this.route + '/' + campaignId + '/news/' + campaignNewsId, body);
  }

  deleteCampaignNews(campaignId: number, campaignNewsId: number): Observable<any> {
    return this.http.delete(this.route + '/' + campaignId + '/news/' + campaignNewsId);
  }

  getEvents(
    campaignId: number,
    currentPage: number,
    take: number,
  ): Observable<PagedResult<CampaignEventModel>> {
    const params = {
      currentPage: currentPage.toString(),
      take: take.toString(),
    };

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route + '/' + campaignId + '/events', { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data ? data.result.data : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  addEvent(event: CampaignEvent, campaignId: number): Observable<any> {
    let formData = new FormData();
    formData.append('title', event.title);
    formData.append('startDate', event.startDate);
    formData.append('endDate', event.endDate);
    formData.append('eventTags', JSON.stringify(event.eventTags));
    formData.append('eventLink', event.eventLink);
    formData.append(
      'AttachmentInfo.EncryptedAttachmentInfo',
      event.attachmentInfo.encryptedAttachmentInfo.toString(),
    );
    formData.append('AttachmentInfo.Token', event.attachmentInfo.token.toString());
    //FRC-360
    formData.append('eventDescription', event.eventDescription);
    formData.append('imageType', event.imageType);
    formData.append('issuerZoomEmail', event.issuerZoomEmail);
    formData.append('eventUrlType', event.eventUrlType);
    return this.http.post(this.route + '/' + campaignId + '/events', formData);
  }

  deleteEvent(campaignEventId: number, campaignId: number): Observable<any> {
    return this.http.delete(this.route + '/' + campaignId + '/events/' + campaignEventId);
  }

  udpateEvent(event: CampaignEventModel, campaignId: number, eventId: number): Observable<any> {
    let formData = new FormData();
    formData.append('title', event.title);
    formData.append('startDate', event.startDate);
    formData.append('endDate', event.endDate);
    formData.append('eventTags', JSON.stringify(event.eventTags));
    formData.append('eventLink', event.eventLink);
    formData.append(
      'AttachmentInfo.EncryptedAttachmentInfo',
      event.attachmentInfo ? event.attachmentInfo.encryptedAttachmentInfo.toString() : '',
    );
    formData.append(
      'AttachmentInfo.Token',
      event.attachmentInfo ? event.attachmentInfo.token.toString() : '',
    );
    //FRC-360
    formData.append('eventDescription', event.eventDescription);
    formData.append('imageType', event.imageType);
    formData.append('issuerZoomEmail', event.issuerZoomEmail);
    formData.append('eventUrlType', event.eventUrlType);
    return this.http.put(this.route + '/' + campaignId + '/events/' + eventId, formData);
  }

  getCampaignInvestmentInfo(campaignId: number): Observable<any> {
    return this.http.get(this.route + '/' + campaignId + '/invest/info').pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getCampaignInvestorCount(campaignId: number): Observable<any> {
    const params =
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
        ? { 'noToken': 'noToken' }
        : null;

    return this.http.get(this.route + '/' + campaignId + '/investorCount', { params: params }).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getCampaignInvestorList(
    campaignId: number,
    currentPage: number,
    take: number,
    isReport: boolean,
    sortBy: string,
    sortDirection: SortDirection,
    status?: string,
    Types?: string,
    referralCode?: string,
  ): Observable<PagedResult<InvestmentModel>> {
    let params: any = {
      currentPage: currentPage.toString(),
      take: take.toString(),
      isGenerateReport: isReport.toString(),
      orderBy: sortBy,
      IsOrderByDesc: sortDirection === SortDirection.Desc,
    };

    if (status) {
      params.status = status;
    }
    if (Types) {
      params.Types = Types;
    }
    if (referralCode) {
      params.referralCode = referralCode;
    }

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route + '/' + campaignId + '/investments', { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data ? data.result.data : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  /**
   * Private helper to build investor list request params and execute the API call
   * Consolidates duplicated logic between getCampaignInvestorsList and getCampaignShareholderList
   */
  private getInvestorListWithEndpoint(
    campaignId: number,
    currentPage: number,
    take: number,
    isReport: boolean,
    sortBy: string,
    sortDirection: SortDirection,
    endpoint: string,
    status?: string,
    Types?: string,
    referralCode?: string,
    PaymentTo?: string,
    InvestorName?: string,
    ReviewStatus?: string,
  ): Observable<PagedResult<InvestmentModel>> {
    let params: any = {
      currentPage: currentPage.toString(),
      take: take.toString(),
      isGenerateReport: isReport.toString(),
      orderBy: sortBy,
      IsOrderByDesc: sortDirection === SortDirection.Desc,
    };

    if (status) {
      params.status = status;
    }
    if (Types) {
      params.Types = Types;
    }
    if (referralCode) {
      params.referralCode = referralCode;
    }
    if (PaymentTo) {
      params.PaymentTo = PaymentTo;
    }
    if (InvestorName) {
      params.InvestorName = InvestorName;
    }
    if (ReviewStatus) {
      params.ReviewStatus = ReviewStatus;
    }

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http.get(this.route + '/' + campaignId + endpoint, { params: params }).pipe(
      map((data: any) => {
        return {
          data: data.result.data ? data.result.data : [],
          total: data.result.totalCount,
        };
      }),
    );
  }

  getCampaignInvestorsList(
    campaignId: number,
    currentPage: number,
    take: number,
    isReport: boolean,
    sortBy: string,
    sortDirection: SortDirection,
    status?: string,
    Types?: string,
    referralCode?: string,
    PaymentTo?: string,
    InvestorName?: string,
    ReviewStatus?: string,
  ): Observable<PagedResult<InvestmentModel>> {
    return this.getInvestorListWithEndpoint(
      campaignId,
      currentPage,
      take,
      isReport,
      sortBy,
      sortDirection,
      '/investorList',
      status,
      Types,
      referralCode,
      PaymentTo,
      InvestorName,
      ReviewStatus,
    );
  }

  getCampaignShareholderList(
    campaignId: number,
    currentPage: number,
    take: number,
    isReport: boolean,
    sortBy: string,
    sortDirection: SortDirection,
    status?: string,
    Types?: string,
    referralCode?: string,
    PaymentTo?: string,
    InvestorName?: string,
    ReviewStatus?: string,
  ): Observable<PagedResult<InvestmentModel>> {
    return this.getInvestorListWithEndpoint(
      campaignId,
      currentPage,
      take,
      isReport,
      sortBy,
      sortDirection,
      '/shareholderList',
      status,
      Types,
      referralCode,
      PaymentTo,
      InvestorName,
      ReviewStatus,
    );
  }

  approveShareReview(
    campaignId: number,
    shareholderRowId: number,
  ): Observable<number> {
    return this.http
      .put(this.route + '/' + campaignId + '/shareholderList/' + shareholderRowId + '/approve', null)
      .pipe(
        map((data: any) => {
          return data?.result ?? data;
        }),
      );
  }

  createShareReviewRequest(campaignId: number, shareId: number, remarks: string): Observable<any> {
    return this.http
      .post(this.route + '/' + campaignId + '/shareholderList/' + shareId + '/review', {
        reviewMessage: remarks,
      })
      .pipe(
        map((data: any) => {
          return data?.result ?? data?.Result ?? data;
        }),
      );
  }

  updateShareReviewRequest(campaignId: number, shareId: number, remarks: string): Observable<any> {
    return this.http
      .put(this.route + '/' + campaignId + '/shareholderList/' + shareId + '/review', {
        reviewMessage: remarks,
      })
      .pipe(
        map((data: any) => {
          return data?.result ?? data?.Result ?? data;
        }),
      );
  }

  getShareReviewRequest(
    campaignId: number,
    shareId: number,
  ): Observable<{
    exists: boolean;
    reviewId: number | null;
    reviewMessage: string | null;
    modifyAt?: string | null;
  }> {
    return this.http
      .get(this.route + '/' + campaignId + '/shareholderList/' + shareId + '/review')
      .pipe(
        map((data: any) => {
          return data?.result ?? data;
        }),
      );
  }

  bulkApproveShareReview(
    campaignId: number,
    investmentIds: number[],
  ): Observable<number> {
    return this.http
      .put(this.route + '/' + campaignId + '/shareholderList/approve', { investmentIds })
      .pipe(
        map((data: any) => {
          return data?.result ?? data;
        }),
      );
  }

  invest(
    campaignId: number,
    lotAmount: number,
    referralCode: string,
    continueInvestment: boolean,
    shareType: ShareType,
  ): Observable<any> {
    const body = {
      LotQuantity: lotAmount,
      ReferralCode: referralCode,
      ContinueInvestment: continueInvestment,
      ShareType: shareType,
    };
    return this.http.post(this.route + '/' + campaignId + '/invest', body);
  }

  setInvestmentPayment(
    campaignId: number,
    investmentId: number,
    paymentModel: WalletPayment,
  ): Observable<any> {
    const body = (
      'attachmentInfo' in paymentModel ||
      'attachmentInfoList' in paymentModel ||
      'emailAddress' in paymentModel ||
      'businessModel' in paymentModel ||
      'bankID' in paymentModel ||
      'tickerCode' in paymentModel
    )
      ? new PaymentModel(paymentModel as Payment)
      : new SimplePaymentModel(paymentModel);

    return this.http
      .post(this.route + '/' + campaignId + '/invest/' + investmentId + '/paymentType', body)
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  getInvestment(campaignId: number, investmentId: number): Observable<InvestmentModel> {
    return this.http.get(this.route + '/' + campaignId + '/invest/' + investmentId).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  updateRssFeedUrl(campaignId: number, rssUrl: string): Observable<any> {
    const body = {
      RSSUrl: rssUrl,
    };

    return this.http.put(this.route + '/' + campaignId + '/rss', body).pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  getCampaignMyInvestments(slug: string): Observable<any> {
    return this.http.get(this.route + '/' + slug + '/myInvestments').pipe(
      map((data: any) => {
        return data.result;
      }),
    );
  }

  uploadSourceOfWealthDocument(
    slug: string,
    investmentId: number,
    investmentEddId: number,
    file: File,
  ): Observable<any> {
    const body = {
      FileName: file.name,
    };

    return this.http
      .post(
        this.route +
          '/' +
          slug +
          '/myInvestments/' +
          investmentId +
          '/edd/' +
          investmentEddId +
          '/attachment/sourceOfWealth',
        body,
      )
      .pipe(
        mergeMap((data: any) => {
          if (data && data.result) return this.accountService.uploadImage(data.result, file);
          else return data.result;
        }),
      );
  }

  uploadSourceOfFundsDocument(
    slug: string,
    investmentId: number,
    investmentEddId: number,
    file: File,
  ): Observable<any> {
    const body = {
      FileName: file.name,
    };

    return this.http
      .post(
        this.route +
          '/' +
          slug +
          '/myInvestments/' +
          investmentId +
          '/edd/' +
          investmentEddId +
          '/attachment/sourceOfFunds',
        body,
      )
      .pipe(
        mergeMap((data: any) => {
          if (data && data.result) return this.accountService.uploadImage(data.result, file);
          else return data.result;
        }),
      );
  }

  addEdd(slug: string, investmentId: number, investmentEddId: number, edd: Edd): Observable<any> {
    return this.http.post(
      this.route +
        '/' +
        slug +
        '/myInvestments/' +
        investmentId +
        '/edd/' +
        investmentEddId +
        '/form',
      edd,
    );
  }

  getEddForm(
    slug: string,
    investmentId: number,
    investmentEddId: number,
    investmentEddFormId: number,
  ): Observable<any> {
    return this.http
      .get(
        this.route +
          '/' +
          slug +
          '/myInvestments/' +
          investmentId +
          '/edd/' +
          investmentEddId +
          '/form/' +
          investmentEddFormId,
      )
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  getFinancialOverview(
    campaignId: number,
    currentPage: number,
    take: number,
  ): Observable<PagedResult<FinancialOverview>> {
    let params = {
      currentPage: currentPage.toString(),
      take: take.toString(),
    };

    if (
      !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
    )
      params['noToken'] = 'noToken';

    return this.http
      .get(this.route + '/' + campaignId + '/financialoverview', { params: params })
      .pipe(
        map((data: any) => {
          return {
            data: data.result.data ? data.result.data : [],
            total: data.result.totalCount,
          };
        }),
      );
  }

  setCampaignViewCount(campaignId: number): Observable<any> {
    const params = {
      noToken: this.storageService.role ? null : 'noToken',
    };

    return this.http.post(this.route + '/' + campaignId + '/campaignView', null, {
      params: params,
    });
  }

  getCampaignDraft(campaignId: number): Observable<CampaignInfo> {
    return this.http.get(this.route + '/' + campaignId + '/campaignDetail/draft').pipe(
      map((data: any) => {
        data.result.campaignWhatsNew = this.getCampaignWhatsNew(data.result.campaignId);
        return data.result;
      }),
    );
  }

  submitCampaignDraft(campaignId: number, campaignInfo: CampaignInfo): Observable<any> {
    return this.http
      .put(this.route + '/' + campaignId + '/campaignDetail/draft', campaignInfo)
      .pipe(
        map((data: any) => {
          return data.result;
        }),
      );
  }

  deleteCampaignDraft(campaignId: number): Observable<any> {
    return this.http.delete(this.route + '/' + campaignId + '/campaignDetail/draft');
  }

  getPosthogViewCount(slug: string): Observable<{ viewCount: number }> {
    return this.http.get<{ viewCount: number }>(`${this.route}/posthog/${slug}/viewCount`);
  }

  getCampaignSummaryBySlug(slug: string): Observable<any> {
    if (!this.storageService.role) return of(null);
    return this.http.get(`${this.route}/${slug}/summary`).pipe(
      map((data: any) => data.result),
    );
  }

  getCampaignSummaryById(campaignId: number): Observable<any> {
    if (!this.storageService.role) return of(null);
    const route = environment.apiServer + 'Api/v1/ECF/{role}/summary/campaign/' + campaignId;
    return this.http.get(route).pipe(
      map((data: any) => data.result),
    );
  }

  reviewCampaignSummary(slug: string, approved: boolean, remark?: string): Observable<any> {
    if (!this.storageService.role) return of(null);
    return this.http.put(`${this.route}/${slug}/summary/review`, { approved, remark }).pipe(
      map((data: any) => data.result),
    );
  }

  getPublicCampaignSummary(slug: string): Observable<any> {
    const params = !this.storageService.role ||
      ![Role.IndividualInvestor, Role.CompanyRep, Role.IssuerRep].includes(this.storageService.role)
      ? { 'noToken': 'noToken' }
      : null;
    return this.http.get(environment.apiServer + 'Api/v1/ECF/public/campaigns/' + slug + '/summary', { params }).pipe(
      map((data: any) => data.result),
    );
  }

  getPosthogCTACount(slug: string): Observable<{
    QnA: number;
    Events: number;
    shareCount: number;
    CtaCount: number;
    fileCount: number;
  }> {
    return this.http.get<{
      QnA: number;
      Events: number;
      shareCount: number;
      CtaCount: number;
      fileCount: number;
    }>(`${this.route}/posthog/${slug}/CTACount`);
  }
}
